import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  type AgedPayableRow,
  type AgedReport,
  downloadAgedPayablesXlsx,
  getAgedPayables,
} from "@/lib/api";
import { AgedBucketsTable, AgedReportError } from "./AgedTables";
import { ReportBackButton } from "@/features/reports/ReportBackButton";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Applied as-of date carried in the URL so drilling into a bill and
 * returning restores the same report (Link keeps the current search). */
function storedAsOf(search: unknown): string {
  if (search && typeof search === "object") {
    const value = (search as Record<string, unknown>).as_of;
    if (typeof value === "string") return value;
  }
  return "";
}

export function AgedPayablesPage() {
  const locationSearch = useRouterState({
    select: (state) => state.location.search,
  });
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState(() => storedAsOf(locationSearch) || todayIso());
  const [applied, setApplied] = useState(asOf);
  const [report, setReport] = useState<AgedReport<AgedPayableRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const run = useCallback((date: string) => {
    setLoading(true);
    setError(null);
    getAgedPayables(date ? { as_of: date } : {})
      .then(setReport)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const date = asOf.trim();
    setApplied(date);
    void navigate({
      to: "/aged-payables",
      replace: true,
      search: (previous) => ({ ...previous, as_of: date || undefined }),
    });
  };

  const exportXlsx = async () => {
    // Dedicated aged-payables workbook twin
    // (`GET /reports/aged-payables.xlsx`) for the applied as-of date.
    setExporting(true);
    setError(null);
    try {
      await downloadAgedPayablesXlsx(applied ? { as_of: applied } : {});
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Aged payables</h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Open bills bucketed by days overdue (Current / 1-30 / 31-60 / 61-90 /
            90+).
            {report ? ` As of ${report.as_of}.` : null}
          </p>
        </div>
        <ReportBackButton />
      </div>

      {error ? (
        <AgedReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <div className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5">
        <form
          onSubmit={submit}
          aria-label="Aged payables filters"
          className="flex flex-wrap items-end gap-3"
        >
          <label htmlFor="ap-as-of" className="grid gap-1 text-xs font-medium">
            As-of date (optional)
            <Input
              id="ap-as-of"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              disabled={loading}
            />
          </label>
          <Button type="submit" disabled={loading}>
            {loading ? "Running..." : "Run report"}
          </Button>
        </form>
        <span
          className="ms-auto"
          title="Download the aged-payables workbook (.xlsx) for the applied date"
        >
          <Button
            type="button"
            variant="secondary"
            onClick={exportXlsx}
            disabled={exporting || loading}
          >
            {exporting ? "Exporting..." : "Export (XLSX)"}
          </Button>
        </span>
      </div>

      {loading && report === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : report ? (
        <AgedBucketsTable
          buckets={report.buckets}
          grandTotalCents={report.grand_total_cents}
          numberLabel="Bill"
          nameLabel="Supplier"
          rowsFor={(bucket) =>
            (bucket.bills ?? []).map((row) => ({
              key: row.id,
              number: row.bill_number,
              name: row.supplier_name,
              dueDate: String(row.due_date).slice(0, 10),
              daysOverdue: row.days_overdue,
              amountDueCents: row.amount_due_cents,
            }))
          }
          emptyMessage="No open bills with a remaining balance."
          numberLink={(row) => (
            <Link
              to="/bills/$billId"
              params={{ billId: row.key }}
              search={(previous) => ({ ...previous })}
              title={`Open bill ${row.number}`}
              className="hover:underline"
            >
              {row.number}
            </Link>
          )}
        />
      ) : null}
    </div>
  );
}
